import React from 'react'
import classNames from 'classnames'
import { type TrendDirection } from '../store/insights'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { fmt, formatHighlightNumber } from '../utils/insightsUtils'

export const InsightsSection: React.FC<{
  title: string
  subtitle: string
  className?: string
  headerRight?: React.ReactNode
  children: React.ReactNode
}> = ({ title, subtitle, className, headerRight, children }) => (
  <div className={classNames('insights-section', className)}>
    <div className='insights-section__header'>
      <div>
        <h3 className='insights-section-title'>{title}</h3>
        <p className='insights-section-subtitle'>{subtitle}</p>
      </div>
      {headerRight}
    </div>
    {children}
  </div>
)

export const InfoTooltip: React.FC<{ text: string; multiline?: boolean }> = ({ text, multiline }) => {
  const isMultiline = multiline ?? text.includes('\n')
  const className = isMultiline ? 'insights-info-tooltip insights-info-tooltip--multiline' : 'insights-info-tooltip'
  return (
    <Tooltip title={text} placement='top' className={className}>
      <i className='bi bi-question-circle text-muted insights-tooltip__icon' role='img' tabIndex={0} aria-label={text} />
    </Tooltip>
  )
}

const UNSPECIFIED_MEETING_TYPE_TOOLTIP = "Meeting type wasn't defined when the data was captured"

export const MeetingTypeName: React.FC<{ name: string | null | undefined }> = ({ name }) => {
  const trimmed = (name ?? '').trim()
  if (trimmed) return <>{trimmed}</>
  return (
    <Tooltip title={UNSPECIFIED_MEETING_TYPE_TOOLTIP} placement='top'>
      <span tabIndex={0}>Unspecified</span>
    </Tooltip>
  )
}
export const TrendBadge: React.FC<{ value: number | null; direction: TrendDirection }> = ({ value, direction }) => {
  if (value == null || direction == null) return <span className='insights-trend-na'>No previous data</span>
  const prefix = value > 0 ? '+' : ''
  return (
    <span
      className={classNames('insights-trend-badge', {
        'insights-trend-badge--up': direction === 'up',
        'insights-trend-badge--down': direction === 'down',
        'insights-trend-badge--neutral': direction === 'neutral',
      })}
    >{`${prefix}${fmt(value)}`}</span>
  )
}

export const SortIcon: React.FC<{ dir: 'asc' | 'desc' }> = ({ dir }) => (
  <i className={classNames('bi', 'insights-sort-icon', dir === 'desc' ? 'bi-sort-down-alt' : 'bi-sort-up-alt')} />
)

export const EmptyState: React.FC<{ icon: React.ReactNode; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <div className='insights-empty-state'>
    <div className='insights-empty-state__icon-box'>{icon}</div>
    <div className='insights-empty-state__text'>
      <div className='insights-empty-state__title'>{title}</div>
      {subtitle && <div className='insights-empty-state__subtitle'>{subtitle}</div>}
    </div>
  </div>
)

export const NumericTrendBadge: React.FC<{ value: number }> = ({ value }) => {
  if (value === 0) return null
  const isUp = value > 0
  return (
    <span
      className={classNames('insights-asset-opp__trend-badge', { 'insights-asset-opp__trend-badge--up': isUp, 'insights-asset-opp__trend-badge--down': !isUp })}
    >
      {isUp ? `+${formatHighlightNumber(value)}` : formatHighlightNumber(value)}
    </span>
  )
}
